import { TrashIcon } from "@heroicons/react/24/outline"
import { useState } from "react"
import { useApp } from "../../AppProvider"
import { useCreateCondition, useUpdateCondition, type Condition } from "../../hooks/useConditions"
import { Button } from "../common"

interface Props {
  organizationId: string
  condition?: Condition
  onSuccess: () => void
}

export const ConditionForm = ({ organizationId, condition, onSuccess }: Props) => {
  const { addToast } = useApp()
  const { token } = useApp()

  const [name, setName] = useState(condition?.name ?? "")
  const [recommendations, setRecommendations] = useState<{ message: string }[]>(
    condition?.recommendations ?? [{ message: "" }],
  )

  const createConditionMutation = useCreateCondition(token)
  const updateConditionMutation = useUpdateCondition(organizationId, token)

  const isEditing = !!condition

  const handleAddRecommendation = () => {
    setRecommendations([...recommendations, { message: "" }])
  }

  const handleRemoveRecommendation = (index: number) => {
    setRecommendations(recommendations.filter((_, i) => i !== index))
  }

  const handleRecommendationChange = (index: number, field: "message", value: string) => {
    const updated = recommendations.map((rec, i) => (i === index ? { ...rec, [field]: value } : rec))
    setRecommendations(updated)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!name.trim()) {
      addToast("Error", "Condition name is required", "error")
      return
    }

    // Filter out completely empty messages
    const filteredRecommendations = recommendations.filter((rec) => rec.message.trim() !== "")

    try {
      if (isEditing) {
        await updateConditionMutation.mutateAsync({
          id: condition._id,
          name: name.trim(),
          recommendations: filteredRecommendations,
        })
        addToast("Success", "Condition updated successfully", "success")
      } else {
        await createConditionMutation.mutateAsync({
          name: name.trim(),
          recommendations: filteredRecommendations,
          organizationId,
        })
        addToast("Success", "Condition created successfully", "success")
      }
      onSuccess()
    } catch (error: any) {
      addToast("Error", error.message || "Failed to save condition", "error")
    }
  }

  const isLoading = createConditionMutation.isPending || updateConditionMutation.isPending

  return (
    <div className="min-w-2xl">
      <h3 className="mb-4 text-lg font-semibold text-gray-900">
        {isEditing ? "Edit condition" : "New condition"}
      </h3>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="conditionName" className="label">
            Name
          </label>
          <input
            type="text"
            id="conditionName"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="input"
            autoComplete="off"
            required
          />
        </div>

        <div>
          <div className="flex items-center justify-between">
            <label className="label">Messages</label>
            <Button type="button" variant="outline" onClick={handleAddRecommendation}>
              Add message
            </Button>
          </div>

          <div className="mt-3 max-h-[60vh] space-y-4 overflow-y-auto">
            {recommendations.map((rec, index) => (
              <div key={index} className="rounded-md border border-gray-200 bg-gray-50 px-4 py-2">
                <div className="flex gap-3">
                  <div className="flex-1">
                    <textarea
                      id={`message-${index}`}
                      value={rec.message}
                      onChange={(e) => handleRecommendationChange(index, "message", e.target.value)}
                      className="input h-20"
                      rows={3}
                      placeholder="Enter message..."
                    />
                  </div>
                  {recommendations.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveRecommendation(index)}
                      className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-gray-600 transition-colors hover:bg-red-100 hover:text-red-600"
                      aria-label={`Remove message ${index + 1}`}>
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
          <Button type="button" variant="outline" onClick={onSuccess} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? "Saving..." : isEditing ? "Update condition" : "Create condition"}
          </Button>
        </div>
      </form>
    </div>
  )
}
